import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { CrossmintPaymentElement, useCrossmintEvents } from '@crossmint/client-sdk-vue-ui';
import { BigNumber } from '@ethersproject/bignumber';
import { formatEther, parseUnits } from '@ethersproject/units';
import { computed, ref } from 'vue';
import { extractProxyData } from '@/lib/claimUtil';
import { useClaimStore } from '@/store/claimStore';

interface CardPaymentFormProps {
  email: string;
  onPaymentStarted: () => void;
  onSuccess: (paymentMethodType: string) => void;
  onError?: (error: string) => void;
  onCostChanged: (priceSubtotalWithManifoldFees: BigNumber, finalPrice: BigNumber) => void;
  onRecipientWalletChanged: (wallet: string) => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CardPaymentForm',
  props: {
    email: {},
    onPaymentStarted: { type: Function },
    onSuccess: { type: Function },
    onError: { type: Function },
    onCostChanged: { type: Function },
    onRecipientWalletChanged: { type: Function }
  },
  setup(__props: any) {

const props = __props;

_useCssVars(_ctx => ({
  "155a7fc5": (cardHeight.value)
}))



const store = useClaimStore();

const cardHeight = ref(1);

const mintConfig = computed(() => {
  return {
    type: store.claimType,
    totalPrice: formatEther(store.finalPrice),
    creatorContractAddress: store.creatorContractAddress,
    instanceId: store.claimIndex,
    mintCount: store.tokensToPurchase,
    mintIndices: store.claimableMintIndices?.length
      ? store.claimableMintIndices.slice(0, store.tokensToPurchase)
      : [],
    // Need to extract the proxy data from the merkle proofs
    merkleProofs: store.claimableMerkleProofs?.length
      ? extractProxyData(store.claimableMerkleProofs.slice(0, store.tokensToPurchase))
      : [],
    contractAddress: store.overrideMintExtensionAddress
      ? store.overrideMintExtensionAddress
      : store.extensionAddress,
  };
});

const mintingEvents = (event: any) => {
  switch (event.type) {
    case 'order:process.finished':
      // All transactions have finished
      props.onSuccess(event.payload.paymentMethodType);
      break;
    case 'transaction:fulfillment.succeeded':
      // A transaction has finished
      break;
    case 'transaction:fulfillment.failed':
      // A transaction has failed
      props.onError?.(event.payload.error);
      break;
    case 'order:process.started':
      // Capturing NFTs process has started
      break;
  }
};

const recipient = computed(() => {
  if (store.mintForWallet) {
    return { email: props.email, wallet: store.mintForWallet };
  }

  return { email: props.email };
});

const environment = computed(() => {
  if ([1, 10, 8453].includes(store.networkId)) {
    return 'production';
  }

  return 'staging';
});

const { listenToMintingEvents } = useCrossmintEvents({ environment: environment.value });

const onEvent = (event: any) => {
  switch (event.type) {
    case 'ui:height.changed':
      cardHeight.value = event.height;
      break;
    case 'recipient:wallet.changed':
      props.onRecipientWalletChanged(event.payload.wallet);
      break;
    // CrossMint Payment Events
    case 'payment:preparation.succeeded':
      // Triggered when checkout is ready for payment.
      break;
    case 'payment:process.started':
      // Triggered when the user has finished entering their card details and clicked Pay.
      break;
    case 'quote:status.changed':
      // Triggered when the price is calculated or if the price changes.
      props.onCostChanged(
        parseUnits(event.payload.lineItems[0].price.amount || '0', 2),
        parseUnits(event.payload.totalPrice.amount || '0', 2)
      );
      break;
    case 'payment:process.succeeded':
      // Triggered when payment has been successfully captured.
      // Loading the minting process
      props.onPaymentStarted();

      if (event.payload.orderIdentifier) {
        listenToMintingEvents({ orderIdentifier: event.payload.orderIdentifier }, mintingEvents);
      }
      break;
    case 'payment:rejected':
      // Triggered if a user's card has been rejected.
      props.onError?.(event.payload.error);
      break;
    case 'payment:preparation.failed':
      // Triggered if a failure has occurred after checkout.
      props.onError?.(event.payload.error);
      break;

    // CrossMint Error Events
    case 'payments:collection.sold-out':
      props.onError?.(event.payload.error);
      break;
    case 'payments:collection.sale-ended':
      props.onError?.(event.payload.error);
      break;
    case 'payments:email.missing':
      break;
    case 'payments:user-wallet.limit-reached':
      props.onError?.(event.payload.error);
      break;
    case 'payments:user-wallet.not-whitelisted':
      props.onError?.(event.payload.error);
      break;
    default:
      break;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(CrossmintPaymentElement), {
    class: "card",
    clientId: _unref(store).crossmintClientId,
    environment: environment.value,
    recipient: recipient.value,
    "ui-config": {
      colors: {
        background: 'black',
        backgroundSecondary: 'black',
        backgroundTertiary: 'white',
        textPrimary: 'white',
        textSecondary: 'black',
        textLink: 'gray',
        accent: 'white',
        border: '#404040',
        danger: 'red',
      },
      fontWeightSecondary: 'bold',
      borderRadius: '0px',
      spacingUnit: '0px',
    },
    "mint-config": mintConfig.value,
    onEvent: onEvent
  }, null, 8, ["clientId", "environment", "recipient", "mint-config"]))
}
}

})